<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	let className = '';
	export { className as class };
	import type { Video as VideoType } from '$lib/types/types';
	import Play from './ui/Play.svelte';
	export let video: VideoType;
	export let playlistId: string;
	import Player from '@vimeo/player';
	import { goto } from '$app/navigation';
	import { PlusOutline, CheckOutline } from 'flowbite-svelte-icons';
	import { userStarsStore } from '$lib/store';
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import lock from '$lib/assets/lock.svg';
	import { dev } from '$app/environment';

	$: star = $userStarsStore.filter((s) => s.video_id == video.id).length > 0;

	const imageSize = '640x360';

	let desc = false;
	let height: number;
	let width: number;
	let container: HTMLDivElement;
	let player: Player | null = null;
	let image: HTMLImageElement;
	let isPlayerReady = false;
	let loaded = false;
	let videoInitialized = false;
	let videoError = false;
	let isPlayerLoading = false;

	function initializeVideo() {
		if (videoInitialized || isPlayerLoading) return;
		isPlayerLoading = true;

		// Defer player initialization to next tick to prevent blocking
		setTimeout(async () => {
			try {
				const playerOptions: any = {
					id: video.vimeo_id,
					width: '100%',
					height: '100%',
					loop: true,
					muted: true,
					controls: false,
					responsive: true,
					autoplay: false, // Changed to false initially
					autopause: false,
					background: true
				};

				// In development, skip the oEmbed check
				if (dev) {
					playerOptions.oembed = false;
				}

				videoInitialized = true;
				player = new Player(container, playerOptions);

				await player.ready();
				isPlayerReady = true;
				loaded = true;
				isPlayerLoading = false;

				if (desc) {
					// If mouse is still hovering, start playing
					player.play().catch((error) => {
						console.warn('Error playing video:', error);
					});
				}

				player.on('play', () => {
					if (image) image.style.opacity = '0';
				});

				player.on('pause', () => {
					if (image) image.style.opacity = '1';
				});

				player.on('error', (error) => {
					console.warn('Vimeo player error:', error);
					videoError = true;
					isPlayerLoading = false;
				});
			} catch (error) {
				console.warn('Error creating Vimeo player:', error);
				videoError = true;
				isPlayerLoading = false;
			}
		}, 100); // Small delay to prevent blocking
	}

	function handleMouseEnter() {
		desc = true;
		if (!videoInitialized && !videoError && !isPlayerLoading) {
			initializeVideo();
		} else if (isPlayerReady && loaded && player) {
			player.play().catch((error) => {
				console.warn('Error playing video:', error);
			});
		}
	}

	function handleMouseLeave() {
		desc = false;
		if (isPlayerReady && loaded && player) {
			player.pause().catch((error) => {
				console.warn('Error pausing video:', error);
			});
		}
	}

	async function addVideo(video: VideoType) {
		if (star) {
			$userStarsStore = $userStarsStore.filter((s) => s.video_id != video.id);
			await $page.data.supabase
				.from('video_stars')
				.delete()
				.eq('video_id', video.id)
				.eq('user_id', $page.data.user.id);
		} else {
			$userStarsStore = [...$userStarsStore, { video_id: video.id, user_id: $page.data.user.id }];
			await $page.data.supabase.from('video_stars').insert([
				{
					video_id: video.id,
					user_id: $page.data.user.id
				}
			]);
		}
	}

	let extraHeight = 100; // Additional height for the description

	let isMobileDevice = false;
	onMount(() => {
		isMobileDevice = window.matchMedia('(max-width: 767px)').matches;
		if (isMobileDevice) desc = true;

		return () => {
			if (player) {
				player.destroy().catch((error) => {
					console.warn('Error destroying player:', error);
				});
			}
		};
	});
</script>

<article
	bind:clientWidth={width}
	on:mouseenter={handleMouseEnter}
	on:mouseleave={handleMouseLeave}
	class={twMerge(
		`relative aspect-video w-full overflow-hidden ${desc ? 'z-10' : ''} transition-all duration-300`,
		className
	)}
>
	<div class="relative h-full w-full">
		<!-- Image is always shown first and handles initial loading state -->
		<img
			src={video?.public_image_url
				? video.public_image_url.replace('1920x1080', '640x360')
				: '/api/placeholder/640/360'}
			alt=""
			width="640"
			height="360"
			class="absolute left-0 top-0 z-20 h-full w-full object-cover transition-opacity duration-1000"
			bind:this={image}
		/>

		<!-- Player container is loaded but player initialization is deferred -->
		<div
			bind:this={container}
			class="absolute left-0 top-0 z-10 h-full w-full"
			class:opacity-0={!isPlayerReady}
			class:transition-opacity={true}
			class:duration-1000={true}
		></div>

		<button
			class="absolute left-0 top-0 z-30 h-full w-full"
			on:click|preventDefault|stopPropagation={() => {
				if (playlistId) {
					goto(`/video/${video.permalink_slug}?playlist=${playlistId}`);
				} else {
					goto(`/video/${video.permalink_slug}`);
				}
			}}
		></button>

		<div
			class="absolute left-0 z-40 w-full transition-all duration-300 ease-out"
			class:bottom-0={desc}
			class:top-full={!desc}
			style="height: {extraHeight}px;"
		>
			<button
				on:click|preventDefault|stopPropagation={() => {
					if (playlistId) {
						goto(`/video/${video.permalink_slug}?playlist=${playlistId}`);
					} else {
						goto(`/video/${video.permalink_slug}`);
					}
				}}
				class="h-full w-full"
			>
				<div
					class="flex h-full flex-col justify-between gap-3 bg-black bg-opacity-60 p-3 text-white"
				>
					<div class="flex items-center justify-between sm:shrink-0">
						<Play></Play>

						<button
							on:click|preventDefault|stopPropagation={() => addVideo(video)}
							class="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-950/80 hover:bg-secondary-950"
						>
							{#if $page.data.user}
								{#if star}
									<CheckOutline class="h-5 w-5 outline-none" />
								{:else}
									<PlusOutline class="h-5 w-5 outline-none" />
								{/if}
							{/if}
						</button>
					</div>

					<h2 class="text-left font-mundial text-xs">{video.name}</h2>
				</div>
			</button>
		</div>

		{#if video.locked || video.locked === undefined}
			<div
				class="absolute left-1/2 top-1/2 z-40 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-400/95"
			>
				<img
					src={lock}
					alt="locked"
					class="h-6 w-6 object-contain object-center text-secondary-950"
				/>
			</div>
		{/if}
	</div>
</article>
